import type { WeaponSkinDefinition } from './weapon-skin-types';

/**
 * Knife skin definitions.
 * These use method: 'model-swap' — the entire weapon GLB is replaced.
 * Each knife type has a unique model with its own animations.
 *
 * ID range: 20000-20999
 */
const KNIFE_SKINS: WeaponSkinDefinition[] = [
  // ────────────────────────────────────────────────────────────────────────
  //  Karambit variants
  // ────────────────────────────────────────────────────────────────────────
  {
    id: 20000,
    name: 'Karambit | Fade',
    weaponIds: ['knife'],
    collection: 'Knife Collection',
    rarity: 'special',
    method: 'model-swap',
    modelPath: '/models/weapons/knives/karambit.glb',
    textures: {
      color: 'knives/karambit/fade/color.ktx2',
      normal: 'knives/karambit/fade/normal.ktx2',
      orm: 'knives/karambit/fade/orm.ktx2',
    },
    tint: 0xff8800,
    metalness: 0.85,
    roughness: 0.10,
    wearRange: [0.00, 0.08],
    thumbnailPath: '/textures/weapon-skins/knives/karambit/fade/thumb.webp',
    team: null,
    statTrakEligible: true,
    description: 'A curved blade with a rainbow fade finish. Elegant and deadly.',
  },
  {
    id: 20001,
    name: 'Karambit | Doppler',
    weaponIds: ['knife'],
    collection: 'Knife Collection',
    rarity: 'special',
    method: 'model-swap',
    modelPath: '/models/weapons/knives/karambit.glb',
    textures: {
      color: 'knives/karambit/doppler/color.ktx2',
      normal: 'knives/karambit/doppler/normal.ktx2',
      orm: 'knives/karambit/doppler/orm.ktx2',
    },
    tint: 0x3344ff,
    metalness: 0.90,
    roughness: 0.08,
    wearRange: [0.00, 0.08],
    thumbnailPath: '/textures/weapon-skins/knives/karambit/doppler/thumb.webp',
    team: null,
    statTrakEligible: true,
    description: 'Deep blue and black swirls on polished steel.',
  },
  {
    id: 20002,
    name: 'Karambit | Crimson Web',
    weaponIds: ['knife'],
    collection: 'Knife Collection',
    rarity: 'special',
    method: 'model-swap',
    modelPath: '/models/weapons/knives/karambit.glb',
    textures: {
      color: 'knives/karambit/crimson-web/color.ktx2',
      normal: 'knives/karambit/crimson-web/normal.ktx2',
      orm: 'knives/karambit/crimson-web/orm.ktx2',
    },
    tint: 0xcc0000,
    roughness: 0.30,
    metalness: 0.20,
    wearRange: [0.06, 0.80],
    thumbnailPath: '/textures/weapon-skins/knives/karambit/crimson-web/thumb.webp',
    team: null,
    statTrakEligible: true,
    description: 'Deep red with black web pattern. The original grail.',
  },

  // ────────────────────────────────────────────────────────────────────────
  //  Butterfly Knife variants
  // ────────────────────────────────────────────────────────────────────────
  {
    id: 20010,
    name: 'Butterfly Knife | Fade',
    weaponIds: ['knife'],
    collection: 'Knife Collection',
    rarity: 'special',
    method: 'model-swap',
    modelPath: '/models/weapons/knives/butterfly.glb',
    textures: {
      color: 'knives/butterfly/fade/color.ktx2',
      normal: 'knives/butterfly/fade/normal.ktx2',
      orm: 'knives/butterfly/fade/orm.ktx2',
    },
    tint: 0xff8800,
    metalness: 0.85,
    roughness: 0.10,
    wearRange: [0.00, 0.08],
    thumbnailPath: '/textures/weapon-skins/knives/butterfly/fade/thumb.webp',
    team: null,
    statTrakEligible: true,
    description: 'A balisong with a mesmerizing rainbow fade.',
  },
  {
    id: 20011,
    name: 'Butterfly Knife | Marble Fade',
    weaponIds: ['knife'],
    collection: 'Knife Collection',
    rarity: 'special',
    method: 'model-swap',
    modelPath: '/models/weapons/knives/butterfly.glb',
    textures: {
      color: 'knives/butterfly/marble-fade/color.ktx2',
      normal: 'knives/butterfly/marble-fade/normal.ktx2',
      orm: 'knives/butterfly/marble-fade/orm.ktx2',
    },
    metalness: 0.90,
    roughness: 0.08,
    wearRange: [0.00, 0.08],
    thumbnailPath: '/textures/weapon-skins/knives/butterfly/marble-fade/thumb.webp',
    team: null,
    statTrakEligible: true,
    description: 'Swirled marble pattern in red, blue, and gold.',
  },

  // ────────────────────────────────────────────────────────────────────────
  //  M9 Bayonet variants
  // ────────────────────────────────────────────────────────────────────────
  {
    id: 20020,
    name: 'M9 Bayonet | Tiger Tooth',
    weaponIds: ['knife'],
    collection: 'Knife Collection',
    rarity: 'special',
    method: 'model-swap',
    modelPath: '/models/weapons/knives/m9-bayonet.glb',
    textures: {
      color: 'knives/m9-bayonet/tiger-tooth/color.ktx2',
      normal: 'knives/m9-bayonet/tiger-tooth/normal.ktx2',
      orm: 'knives/m9-bayonet/tiger-tooth/orm.ktx2',
    },
    tint: 0xffcc00,
    metalness: 0.95,
    roughness: 0.05,
    wearRange: [0.00, 0.08],
    thumbnailPath: '/textures/weapon-skins/knives/m9-bayonet/tiger-tooth/thumb.webp',
    team: null,
    statTrakEligible: true,
    description: 'Bright gold tiger stripe on mirror-polished steel.',
  },
  {
    id: 20021,
    name: 'M9 Bayonet | Slaughter',
    weaponIds: ['knife'],
    collection: 'Knife Collection',
    rarity: 'special',
    method: 'model-swap',
    modelPath: '/models/weapons/knives/m9-bayonet.glb',
    textures: {
      color: 'knives/m9-bayonet/slaughter/color.ktx2',
      normal: 'knives/m9-bayonet/slaughter/normal.ktx2',
      orm: 'knives/m9-bayonet/slaughter/orm.ktx2',
    },
    tint: 0xcc3333,
    metalness: 0.80,
    roughness: 0.12,
    wearRange: [0.01, 0.26],
    thumbnailPath: '/textures/weapon-skins/knives/m9-bayonet/slaughter/thumb.webp',
    team: null,
    statTrakEligible: true,
    description: 'Red and silver metallic pattern. Diamond and angel patterns are prized.',
  },

  // ────────────────────────────────────────────────────────────────────────
  //  Bayonet variants
  // ────────────────────────────────────────────────────────────────────────
  {
    id: 20030,
    name: 'Bayonet | Doppler',
    weaponIds: ['knife'],
    collection: 'Knife Collection',
    rarity: 'special',
    method: 'model-swap',
    modelPath: '/models/weapons/knives/bayonet.glb',
    textures: {
      color: 'knives/bayonet/doppler/color.ktx2',
      normal: 'knives/bayonet/doppler/normal.ktx2',
      orm: 'knives/bayonet/doppler/orm.ktx2',
    },
    tint: 0x4455ff,
    metalness: 0.90,
    roughness: 0.08,
    wearRange: [0.00, 0.08],
    thumbnailPath: '/textures/weapon-skins/knives/bayonet/doppler/thumb.webp',
    team: null,
    statTrakEligible: true,
    description: 'Phase patterns in blue and black on polished steel.',
  },

  // ────────────────────────────────────────────────────────────────────────
  //  Flip Knife variants
  // ────────────────────────────────────────────────────────────────────────
  {
    id: 20040,
    name: 'Flip Knife | Autotronic',
    weaponIds: ['knife'],
    collection: 'Knife Collection',
    rarity: 'special',
    method: 'model-swap',
    modelPath: '/models/weapons/knives/flip-knife.glb',
    textures: {
      color: 'knives/flip-knife/autotronic/color.ktx2',
      normal: 'knives/flip-knife/autotronic/normal.ktx2',
      orm: 'knives/flip-knife/autotronic/orm.ktx2',
    },
    tint: 0xcc2200,
    metalness: 0.70,
    roughness: 0.20,
    wearRange: [0.00, 0.50],
    thumbnailPath: '/textures/weapon-skins/knives/flip-knife/autotronic/thumb.webp',
    team: null,
    statTrakEligible: true,
    description: 'Dark red mechanical pattern with carbon fiber accents.',
  },

  // ────────────────────────────────────────────────────────────────────────
  //  Skeleton Knife
  // ────────────────────────────────────────────────────────────────────────
  {
    id: 20050,
    name: 'Skeleton Knife | Crimson Web',
    weaponIds: ['knife'],
    collection: 'Knife Collection',
    rarity: 'special',
    method: 'model-swap',
    modelPath: '/models/weapons/knives/skeleton.glb',
    textures: {
      color: 'knives/skeleton/crimson-web/color.ktx2',
      normal: 'knives/skeleton/crimson-web/normal.ktx2',
      orm: 'knives/skeleton/crimson-web/orm.ktx2',
    },
    tint: 0xcc0000,
    roughness: 0.30,
    metalness: 0.20,
    wearRange: [0.06, 0.80],
    thumbnailPath: '/textures/weapon-skins/knives/skeleton/crimson-web/thumb.webp',
    team: null,
    statTrakEligible: true,
    description: 'A skeletonized blade with deep red web pattern.',
  },

  // ────────────────────────────────────────────────────────────────────────
  //  Talon Knife
  // ────────────────────────────────────────────────────────────────────────
  {
    id: 20060,
    name: 'Talon Knife | Case Hardened',
    weaponIds: ['knife'],
    collection: 'Knife Collection',
    rarity: 'special',
    method: 'model-swap',
    modelPath: '/models/weapons/knives/talon.glb',
    textures: {
      color: 'knives/talon/case-hardened/color.ktx2',
      normal: 'knives/talon/case-hardened/normal.ktx2',
      orm: 'knives/talon/case-hardened/orm.ktx2',
    },
    pattern: {
      texturePath: 'knives/talon/case-hardened/pattern.ktx2',
      seedRange: [1, 999],
      colorPalette: [0x2244AA, 0xB87333, 0xC0C0C0],
    },
    metalness: 0.85,
    roughness: 0.15,
    wearRange: [0.00, 0.70],
    thumbnailPath: '/textures/weapon-skins/knives/talon/case-hardened/thumb.webp',
    team: null,
    statTrakEligible: true,
    description: 'Heat-treated talon blade with unique blue-gold patterns.',
  },
];

/**
 * Glove skin definitions.
 * These use method: 'texture-swap' — same glove mesh, different textures.
 * Applied to the viewmodel hand/arm meshes.
 *
 * ID range: 21000-21999
 */
const GLOVE_SKINS: WeaponSkinDefinition[] = [
  // ────────────────────────────────────────────────────────────────────────
  //  Sport Gloves
  // ────────────────────────────────────────────────────────────────────────
  {
    id: 21000,
    name: 'Sport Gloves | Hedge Maze',
    weaponIds: ['gloves'],
    collection: 'Glove Collection',
    rarity: 'special',
    method: 'texture-swap',
    textures: {
      color: 'gloves/sport/hedge-maze/color.ktx2',
      normal: 'gloves/sport/hedge-maze/normal.ktx2',
      orm: 'gloves/sport/hedge-maze/orm.ktx2',
    },
    tint: 0x228822,
    roughness: 0.60,
    metalness: 0.05,
    wearRange: [0.06, 0.80],
    thumbnailPath: '/textures/weapon-skins/gloves/sport/hedge-maze/thumb.webp',
    team: null,
    statTrakEligible: false,
    description: 'Green leather with intricate maze pattern.',
  },
  {
    id: 21001,
    name: 'Sport Gloves | Pandora\'s Box',
    weaponIds: ['gloves'],
    collection: 'Glove Collection',
    rarity: 'special',
    method: 'texture-swap',
    textures: {
      color: 'gloves/sport/pandoras-box/color.ktx2',
      normal: 'gloves/sport/pandoras-box/normal.ktx2',
      orm: 'gloves/sport/pandoras-box/orm.ktx2',
    },
    tint: 0x6622aa,
    roughness: 0.50,
    metalness: 0.10,
    wearRange: [0.06, 0.80],
    thumbnailPath: '/textures/weapon-skins/gloves/sport/pandoras-box/thumb.webp',
    team: null,
    statTrakEligible: false,
    description: 'Deep purple with mysterious ancient symbols.',
  },

  // ────────────────────────────────────────────────────────────────────────
  //  Driver Gloves
  // ────────────────────────────────────────────────────────────────────────
  {
    id: 21010,
    name: 'Driver Gloves | Crimson Weave',
    weaponIds: ['gloves'],
    collection: 'Glove Collection',
    rarity: 'special',
    method: 'texture-swap',
    textures: {
      color: 'gloves/driver/crimson-weave/color.ktx2',
      normal: 'gloves/driver/crimson-weave/normal.ktx2',
      orm: 'gloves/driver/crimson-weave/orm.ktx2',
    },
    tint: 0xcc2222,
    roughness: 0.45,
    metalness: 0.08,
    wearRange: [0.06, 0.80],
    thumbnailPath: '/textures/weapon-skins/gloves/driver/crimson-weave/thumb.webp',
    team: null,
    statTrakEligible: false,
    description: 'Rich crimson leather with woven texture detail.',
  },

  // ────────────────────────────────────────────────────────────────────────
  //  Hand Wraps
  // ────────────────────────────────────────────────────────────────────────
  {
    id: 21020,
    name: 'Hand Wraps | Slaughter',
    weaponIds: ['gloves'],
    collection: 'Glove Collection',
    rarity: 'special',
    method: 'texture-swap',
    textures: {
      color: 'gloves/wraps/slaughter/color.ktx2',
      normal: 'gloves/wraps/slaughter/normal.ktx2',
      orm: 'gloves/wraps/slaughter/orm.ktx2',
    },
    tint: 0xdd3333,
    roughness: 0.70,
    metalness: 0.02,
    wearRange: [0.06, 0.80],
    thumbnailPath: '/textures/weapon-skins/gloves/wraps/slaughter/thumb.webp',
    team: null,
    statTrakEligible: false,
    description: 'Blood-red cloth wraps with metallic accents.',
  },

  // ────────────────────────────────────────────────────────────────────────
  //  Specialist Gloves
  // ────────────────────────────────────────────────────────────────────────
  {
    id: 21030,
    name: 'Specialist Gloves | Fade',
    weaponIds: ['gloves'],
    collection: 'Glove Collection',
    rarity: 'special',
    method: 'texture-swap',
    textures: {
      color: 'gloves/specialist/fade/color.ktx2',
      normal: 'gloves/specialist/fade/normal.ktx2',
      orm: 'gloves/specialist/fade/orm.ktx2',
    },
    tint: 0xff8800,
    roughness: 0.35,
    metalness: 0.15,
    wearRange: [0.00, 0.50],
    thumbnailPath: '/textures/weapon-skins/gloves/specialist/fade/thumb.webp',
    team: null,
    statTrakEligible: false,
    description: 'Tactical gloves with a vibrant rainbow fade finish.',
  },
];

/**
 * Combined knife + glove skin definitions.
 * Import this and merge into the weapon skin registry.
 *
 * U-H-003: GLOVE_SKINS are intentionally excluded from the registry export
 * until the texture assets under `client/public/textures/weapon-skins/gloves/`
 * exist. The definitions remain above so they can be re-included by appending
 * `...GLOVE_SKINS` once assets ship — no metadata is lost. Until then, any
 * registry query for a glove skin returns `undefined`, preventing 404s on
 * thumbnails + color/normal/orm channels.
 */
export const KNIFE_GLOVE_SKINS: ReadonlyMap<number, WeaponSkinDefinition> = new Map(
  KNIFE_SKINS.map(skin => [skin.id, skin]),
);

/**
 * All knife type model paths, for preloading.
 */
export const KNIFE_MODEL_PATHS: Record<string, string> = {
  karambit: '/models/weapons/knives/karambit.glb',
  butterfly: '/models/weapons/knives/butterfly.glb',
  'm9-bayonet': '/models/weapons/knives/m9-bayonet.glb',
  bayonet: '/models/weapons/knives/bayonet.glb',
  'flip-knife': '/models/weapons/knives/flip-knife.glb',
  skeleton: '/models/weapons/knives/skeleton.glb',
  talon: '/models/weapons/knives/talon.glb',
};
